import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * DialogContent — defaults to "do NOT close on outside click / Esc".
 *
 * Rationale (2026-05-11): users were losing in-progress form data
 * (implant orders, clinical notes, surgical reports) because Radix's
 * default behaviour is to close a Dialog on outside click. We now require
 * an explicit Cancel / Close action.
 *
 * Opt-out: pass `closeOnOutsideClick={true}` for confirmation dialogs or
 * other transient overlays where outside-click dismissal is desirable.
 * Custom `onPointerDownOutside` / `onInteractOutside` / `onEscapeKeyDown`
 * handlers from the parent are still honoured and can preventDefault()
 * (or not) on their own.
 */
const DialogContent = React.forwardRef((
  {
    className,
    children,
    closeOnOutsideClick = false,
    closeOnEscape = false,
    onPointerDownOutside,
    onInteractOutside,
    onEscapeKeyDown,
    ...props
  },
  ref,
) => {
  const handlePointerDownOutside = (e) => {
    onPointerDownOutside?.(e);
    if (!closeOnOutsideClick && !e.defaultPrevented) e.preventDefault();
  };
  const handleInteractOutside = (e) => {
    onInteractOutside?.(e);
    if (!closeOnOutsideClick && !e.defaultPrevented) e.preventDefault();
  };
  const handleEscapeKeyDown = (e) => {
    onEscapeKeyDown?.(e);
    if (!closeOnEscape && !e.defaultPrevented) e.preventDefault();
  };
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Default `max-h-[90vh] overflow-y-auto` so any dialog whose
          // form exceeds the viewport (e.g. ExpressAppointmentDialog on
          // shorter laptop screens) scrolls instead of getting clipped.
          // Dialogs that need a different cap (e.g. `h-[90vh] flex
          // flex-col`) override via `className` because `cn()` uses
          // tailwind-merge — last value wins per utility group.
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg max-h-[90vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className,
        )}
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
        {...props}>
        {children}
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

// Dialog content without overlay - for nested dialogs.
// Same close-protection defaults as DialogContent above.
const DialogContentNoOverlay = React.forwardRef((
  {
    className,
    children,
    closeOnOutsideClick = false,
    closeOnEscape = false,
    onPointerDownOutside,
    onInteractOutside,
    onEscapeKeyDown,
    ...props
  },
  ref,
) => {
  const handlePointerDownOutside = (e) => {
    onPointerDownOutside?.(e);
    if (!closeOnOutsideClick && !e.defaultPrevented) e.preventDefault();
  };
  const handleInteractOutside = (e) => {
    onInteractOutside?.(e);
    if (!closeOnOutsideClick && !e.defaultPrevented) e.preventDefault();
  };
  const handleEscapeKeyDown = (e) => {
    onEscapeKeyDown?.(e);
    if (!closeOnEscape && !e.defaultPrevented) e.preventDefault();
  };
  return (
    <DialogPortal>
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Same default scroll cap as DialogContent — see comment above.
          "fixed left-[50%] top-[50%] z-[100] grid w-full max-w-lg max-h-[90vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg",
          className,
        )}
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
        {...props}>
        {children}
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContentNoOverlay.displayName = "DialogContentNoOverlay"

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogContentNoOverlay,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
